import React, { memo, useEffect, useMemo, useRef, useState } from "react"
import { DndProvider } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  alpha
} from "@mui/material"
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable
} from "@tanstack/react-table"
import { visuallyHidden } from "@mui/utils"
import { useInView } from "react-intersection-observer"
import { useVirtualizer } from "@tanstack/react-virtual"
import { useDrag, useDrop } from "react-dnd"
import { COLUMN_WIDTH_AUTO, reorderColumns, formatNumber } from "shared/lib"

/**
 * @param {import("@tanstack/react-table").Header} cell
 */
const getHeaderZIndex = (cell) => {
  const ctx = cell.getContext()
  if (cell.column.getIsPinned()) {
    return (
      ctx.table.getVisibleFlatColumns().length +
      ctx.table.getLeftLeafColumns().length +
      ctx.table.getRightLeafColumns().length -
      cell.column.getPinnedIndex() +
      1
    )
  }

  return ctx.table.getVisibleFlatColumns().length - cell.index
}

/**
 * @param {import("@tanstack/react-table").Cell} cell
 * @returns {number}
 */
const getCellZIndex = (cell) => {
  if (cell.column.getIsPinned()) {
    const ctx = cell.getContext()

    return (
      ctx.table.getVisibleFlatColumns().length +
      ctx.table.getLeftLeafColumns().length +
      ctx.table.getRightLeafColumns().length -
      cell.column.getPinnedIndex()
    )
  }

  return 0
}

const SELECTED_TINT_ALPHA = 0.12

const getPinnedSx = (column, positions, isSelected = false) => {
  const isPinned = column.getIsPinned()
  if (!isPinned) {
    return null
  }

  const side = isPinned.toString()
  const pinnedIndex = column.getPinnedIndex()

  return {
    position: "sticky",
    backgroundColor: "white",
    // Pinned cells float above the scrolling table, so they must stay opaque.
    // A flat single-color gradient layers the translucent selection tint over
    // the white base, matching the row color without letting content through.
    ...(isSelected
      ? {
          backgroundImage: (theme) => {
            const tint = alpha(theme.palette.primary.main, SELECTED_TINT_ALPHA)

            return `linear-gradient(${tint}, ${tint})`
          }
        }
      : {}),
    [side]: positions[side][pinnedIndex],
    ...(positions.isBordering(column.id)
      ? {
          boxShadow: `inset ${
            isPinned === "left" ? "-" : ""
          }3px 0px 0 0px #eeeeee`
        }
      : {})
  }
}

/**
 * @param {import("@tanstack/react-table").Table} table
 * @returns
 */
const getStickyPositions = (table) => {
  const leftColumns = table.getLeftLeafColumns()
  const left = [0]

  for (let i = 1; i < leftColumns.length; i++) {
    left.push(leftColumns[i - 1].getSize() + left[i - 1])
  }

  const rightColumns = table.getRightLeafColumns()
  const right = [0]
  for (let i = 1; i < rightColumns.length; i++) {
    right.unshift(
      rightColumns[rightColumns.length - i].getSize() + right[i - 1]
    )
  }
  const delimiters = {
    left: leftColumns?.[leftColumns?.length - 1]?.id,
    right: rightColumns?.[0]?.id
  }

  return {
    left,
    right,
    delimiters,
    isBordering(id) {
      return delimiters.left === id || delimiters.right === id
    }
  }
}

/**
 * @param {object} props
 * @param {import("@tanstack/react-table").Table} props.table
 * @param {import("@tanstack/react-table").Header} props.header
 * @param {object} props.stickyPositions
 * @param {boolean} props.enableOrdering
 */
const DraggableColumnHeader = ({
  header,
  table,
  stickyPositions,
  enableOrdering,
  count = null
}) => {
  const { getState, setColumnOrder, setColumnPinning } = table
  const { columnOrder, columnPinning } = getState()
  const { column } = header
  const cellSx = header.column.columnDef.meta?.cellSx
  const hasCount = count !== null && count !== undefined

  const [, dropRef] = useDrop({
    accept: "column",
    /** @param {import("@tanstack/react-table").Column} draggedColumn */
    drop: (draggedColumn) => {
      const newColumnOrder = reorderColumns(
        draggedColumn.id,
        column.id,
        columnOrder
      )
      setColumnOrder(newColumnOrder)

      if (
        draggedColumn.getIsPinned() === column.getIsPinned() &&
        !!column.getIsPinned()
      ) {
        const newColumnPinning = reorderColumns(
          draggedColumn.id,
          column.id,
          columnPinning[column.getIsPinned().toString()]
        )

        setColumnPinning({
          ...columnPinning,
          [column.getIsPinned().toString()]: newColumnPinning
        })
      }
    }
  })

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    item: () => column,
    type: "column"
  })

  return (
    <TableCell
      ref={enableOrdering ? dropRef : null}
      sx={{
        opacity: isDragging ? 0.5 : 1,
        zIndex: getHeaderZIndex(header),
        ...cellSx,
        ...getPinnedSx(header.column, stickyPositions),
        "&:hover .resizer": {
          display: "block"
        }
      }}
    >
      {hasCount && (
        <Box
          data-testid="table-row-count"
          sx={{
            display: "flex",
            justifyContent: "center",
            whiteSpace: "nowrap",
            fontWeight: 700
          }}
        >
          {formatNumber(count)}
        </Box>
      )}
      <div ref={enableOrdering ? previewRef : null}>
        <div ref={enableOrdering ? dragRef : null}>
          {header.column.columnDef.enableSorting ? (
            <TableSortLabel
              active={!!header.column.getIsSorted()}
              direction={header.column.getIsSorted() || "asc"}
              onClick={header.column.getToggleSortingHandler()}
            >
              {flexRender(header.column.columnDef.header, header.getContext())}

              {header.column.getIsSorted() ? (
                <Box component="span" sx={visuallyHidden}>
                  {header.column.getIsSorted() === "desc"
                    ? "sorted descending"
                    : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          ) : (
            flexRender(header.column.columnDef.header, header.getContext())
          )}
        </div>

        {header.column.columnDef.enableResizing && (
          <Box
            className="resizer"
            sx={{
              marginLeft: "4px",
              position: "absolute",
              right: 0,
              top: 0,
              height: "100%",
              width: "4px",
              background: "#5180DC",
              cursor: "col-resize",
              userSelect: "none",
              touchAction: "none",
              display: "none",
              zIndex: 20
            }}
            style={
              header.column.getIsResizing()
                ? {
                    transform: `translateX(${
                      table.getState().columnSizingInfo.deltaOffset
                    }px)`,
                    background: "blue",
                    opacity: 1,
                    height: "10000vh",
                    width: "1px",
                    display: "block"
                  }
                : null
            }
            onMouseDown={header.getResizeHandler()}
            onTouchStart={header.getResizeHandler()}
          />
        )}
      </div>
    </TableCell>
  )
}

/**
 * Memoized so that virtualizer re-renders during scrolling only render rows
 * entering the viewport. Every value a row renders from must come in as a prop.
 */
const DataTableRow = memo(
  ({
    row,
    cells,
    index,
    measureElement,
    isSelected,
    isExpanded,
    canExpand,
    highlightError,
    stickyPositions,
    onRowClick,
    renderTopRow,
    renderBottomRow,
    renderSubComponent
  }) => (
    <TableBody data-index={index} ref={measureElement}>
      {renderTopRow && renderTopRow(row)}
      <TableRow
        data-row-id={row.id}
        data-selected={isSelected || undefined}
        hover
        selected={isSelected}
        onClick={onRowClick ? () => onRowClick(row.original, row) : null}
        sx={{
          backgroundColor: highlightError ? "#FF00001A" : "initial",
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: (theme) =>
              alpha(theme.palette.primary.main, SELECTED_TINT_ALPHA)
          },
          ...(onRowClick ? { cursor: "pointer" } : {})
        }}
      >
        {cells.map((cell) => {
          const cellSx = cell.column.columnDef.meta?.cellSx

          return (
            <TableCell
              key={cell.id}
              data-column-id={cell.column.id}
              sx={{
                overflow: "hidden",
                zIndex: getCellZIndex(cell),
                ...getPinnedSx(cell.column, stickyPositions, isSelected),
                ...cellSx
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          )
        })}
      </TableRow>
      {renderSubComponent && isExpanded && canExpand && (
        <TableRow>
          <TableCell colSpan={cells.length} sx={{ padding: 0 }}>
            {renderSubComponent({ row })}
          </TableCell>
        </TableRow>
      )}
      {renderBottomRow?.(row)}
    </TableBody>
  )
)

// Fresh fallbacks on every render would invalidate react-table's memoized
// cells and defeat DataTableRow memoization.
const EMPTY_COLUMN_VISIBILITY = {}
const EMPTY_COLUMN_ORDER = []
const EMPTY_COLUMN_PINNING = { left: [], right: [] }

const defaultInitialState = {
  sorting: [],
  columnVisibility: EMPTY_COLUMN_VISIBILITY,
  columnPinning: EMPTY_COLUMN_PINNING,
  columnOrder: null,
  columnSizing: {}
}

/**
 * @param {import("./types").DataTableProps} props
 */
export const DataTable = ({
  sx,
  footerSx,
  wrapperSx,
  tableWidth,
  data,
  meta,
  columns,
  onSort,
  canFetchMore,
  initialState = defaultInitialState,
  stickyHeader = false,
  hideHeader = false,
  withFooter = false,
  getRowId = undefined,
  getIsRowExpanded,
  getIsRowSelected,
  getRowCanExpand,
  fetchMore,
  rootMargin = "500px",
  overscan = 35,
  onStateChange,
  onRowClick,
  renderSubComponent,
  renderTopRow = null,
  renderBottomRow = null,
  total = null,
  ...tableProps
}) => {
  const [sorting, setSorting] = useState(() => initialState.sorting ?? [])
  const [columnSizing, setColumnSizing] = useState(
    () => initialState.columnSizing ?? {}
  )

  useEffect(() => {
    if (onSort) {
      onSort(sorting)
    }
  }, [onSort, sorting])

  const table = useReactTable({
    data: data,
    meta: meta,
    columns: columns,
    state: {
      sorting,
      columnVisibility:
        initialState.columnVisibility ?? EMPTY_COLUMN_VISIBILITY,
      columnOrder: initialState.columnOrder ?? EMPTY_COLUMN_ORDER,
      columnSizing,
      columnPinning: initialState.columnPinning ?? EMPTY_COLUMN_PINNING
    },
    getIsRowExpanded,
    getRowCanExpand,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowId,
    manualSorting: true,
    sortDescFirst: true,
    columnResizeMode: "onEnd",
    onSortingChange: setSorting,
    onColumnOrderChange: (columnOrder) => {
      onStateChange({ ...table.getState(), columnOrder })
    },
    onColumnPinningChange: (columnPinning) => {
      onStateChange({ ...table.getState(), columnPinning })
    },
    onColumnSizingChange: (updater) => {
      const newSizing =
        typeof updater === "function" ? updater(columnSizing) : updater
      setColumnSizing(newSizing)
      onStateChange?.({ ...table.getState(), columnSizing: newSizing })
    },
    enableSortingRemoval: true
  })

  const currentStickyPositions = getStickyPositions(table)
  const stickyPositionsKey = JSON.stringify(currentStickyPositions)
  // A stable reference keeps memoized rows from re-rendering while the pinned layout is unchanged
  const stickyPositions = useMemo(
    () => currentStickyPositions,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stickyPositionsKey]
  )

  const visibleColumns = table.getVisibleLeafColumns()
  const countColumnId =
    visibleColumns.find((column) => column.id === "actions")?.id ??
    visibleColumns[visibleColumns.length - 1]?.id

  const rootRef = useRef(null)
  const { ref: lastRowRef } = useInView({
    root: rootRef.current,
    rootMargin,
    onChange: (inView) => {
      if (inView && fetchMore && canFetchMore) {
        fetchMore()
      }
    }
  })

  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => rootRef.current,
    count: rows.length,
    overscan,
    estimateSize: () => 50
  })
  const virtualRows = rowVirtualizer.getVirtualItems()
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0
  const paddingBottom =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() -
        (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0

  // context={window} keeps react-dnd from dropping its shared manager when the
  // provider count briefly hits zero during navigation, which otherwise throws
  // "Cannot have two HTML5 backends at the same time." (react-dnd#3257)
  return (
    <DndProvider backend={HTML5Backend} context={window}>
      <Box
        sx={{
          height: "100%",
          maxHeight: "100%",
          position: "relative",
          overflow: "hidden",
          ...wrapperSx
        }}
      >
        <TableContainer sx={{ maxHeight: "100%", ...sx }} ref={rootRef}>
          <Table
            stickyHeader={stickyHeader}
            sx={{ tableLayout: "fixed", maxHeight: "100%" }}
            style={{
              width:
                tableWidth ||
                (onStateChange ? table.getCenterTotalSize() : "100%")
            }}
            {...tableProps}
          >
            {!hideHeader && (
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => {
                  return (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        return (
                          <DraggableColumnHeader
                            key={header.id}
                            table={table}
                            stickyPositions={stickyPositions}
                            header={header}
                            enableOrdering={!!onStateChange}
                            count={
                              header.column.id === countColumnId ? total : null
                            }
                          />
                        )
                      })}
                    </TableRow>
                  )
                })}
              </TableHead>
            )}
            {paddingTop > 0 && (
              <TableBody>
                <TableRow>
                  <TableCell style={{ height: `${paddingTop}px` }} />
                </TableRow>
              </TableBody>
            )}
            <colgroup>
              {table.getHeaderGroups().map((headerGroup) => {
                return headerGroup.headers.map((header) => {
                  return (
                    <col
                      key={header.id}
                      width={
                        header.column.columnDef.size === COLUMN_WIDTH_AUTO
                          ? undefined
                          : header.column.getSize()
                      }
                    />
                  )
                })
              })}
            </colgroup>

            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index]
              const highlightError = row
                .getAllCells()
                .some((cell) =>
                  cell.column.columnDef.meta?.getRowError?.(cell.row.index)
                )
              const isSelected = getIsRowSelected?.(row) ?? false

              return (
                <DataTableRow
                  key={virtualRow.key}
                  row={row}
                  cells={row.getVisibleCells()}
                  index={virtualRow.index}
                  measureElement={rowVirtualizer.measureElement}
                  isSelected={isSelected}
                  isExpanded={row.getIsExpanded()}
                  canExpand={row.getCanExpand()}
                  highlightError={highlightError}
                  stickyPositions={stickyPositions}
                  // Unused by the row itself; passed so rows re-render when
                  // meta changes, since cells can read it from their context
                  meta={meta}
                  onRowClick={onRowClick}
                  renderTopRow={renderTopRow}
                  renderBottomRow={renderBottomRow}
                  renderSubComponent={renderSubComponent}
                />
              )
            })}
            <TableBody>
              <TableRow ref={lastRowRef} />
              {paddingBottom > 0 && (
                <TableRow>
                  <TableCell style={{ height: `${paddingBottom}px` }} />
                </TableRow>
              )}
            </TableBody>
            {withFooter && (
              <TableFooter
                sx={{ position: "sticky", zIndex: 200, ...footerSx }}
              >
                {table.getFooterGroups().map((footerGroup) => {
                  return (
                    <TableRow key={footerGroup.id}>
                      {footerGroup.headers.map((header) => {
                        if (!header.column.columnDef.footer) {
                          return null
                        }

                        const footerSx = header.column.columnDef.meta?.footerSx

                        return (
                          <TableCell
                            key={header.id}
                            colSpan={
                              header.column.columnDef.meta?.footerColSpan
                            }
                            sx={{
                              overflow: "hidden",
                              border: "none",
                              zIndex: getHeaderZIndex(header),
                              ...getPinnedSx(header.column, stickyPositions),
                              ...(footerSx ?? {})
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.footer,
                              header.getContext()
                            )}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )
                })}
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Box>
    </DndProvider>
  )
}
